import { useMemo } from "react";

// A seeded sky stays put across form edits, remounts and responsive previews.
const createSky = (vivid) => {
  let seed = 4187;
  const rand = (min, max) => {
    seed = (seed * 16807) % 2147483647;
    return min + (seed / 2147483647) * (max - min);
  };
  const stars = Array.from({ length: vivid ? 340 : 150 }, (_, i) => ({
    top: rand(0, 92), left: rand(0, 100),
    size: i % 17 === 0 ? rand(2.4, 3.2) : rand(.8, 2),
    op: rand(.25, .9),
    color: ["#FFFFFF", "#D2E5FF", "#E8D8FF", "#FFE8CD"][i % 4],
    bright: i % 17 === 0,
  }));
  const sparkles = Array.from({ length: vivid ? 15 : 9 }, () => ({
    top: rand(4, 78), left: rand(4, 96), size: rand(8, 17),
  }));
  const dust = vivid ? Array.from({ length: 520 }, () => {
    const x = rand(0, 1200);
    return { x, y: 630 - x * .58 + (rand(-1, 1) + rand(-1, 1)) * 105, r: rand(.4, 1.2), opacity: rand(.12, .5) };
  }) : [];
  return { stars, sparkles, dust };
};

export const Cosmos = ({ className = "", vivid = false, paused = false }) => {
  const { stars, sparkles, dust } = useMemo(() => createSky(vivid), [vivid]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${vivid ? "cosmos-vivid" : ""} ${className}`} aria-hidden="true" data-paused={paused} data-testid="cosmos-bg">
      {/* deep cosmos base */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #060A22 0%, #0A0F30 44%, #0A0C22 100%)" }} />

      {/* sunset horizon paint-bursts */}
      <div
        className="cosmos-glow cosmos-glow-flame absolute -bottom-40 -left-24 h-[26rem] w-[26rem] rounded-full blur-[90px] opacity-70"
        style={{ background: "radial-gradient(circle, #FF4400 0%, rgba(255,68,0,0) 70%)" }}
      />
      <div
        className="cosmos-glow cosmos-glow-pink absolute -bottom-48 left-1/3 h-[32rem] w-[32rem] rounded-full blur-[110px] opacity-60"
        style={{ background: "radial-gradient(circle, #FF2D7E 0%, rgba(255,45,126,0) 70%)" }}
      />
      <div
        className="cosmos-glow cosmos-glow-amber absolute -bottom-40 -right-16 h-[26rem] w-[26rem] rounded-full blur-[90px] opacity-60"
        style={{ background: "radial-gradient(circle, #FFB020 0%, rgba(255,176,32,0) 70%)" }}
      />
      <div
        className="cosmos-glow cosmos-glow-violet absolute bottom-4 left-1/2 -translate-x-1/2 h-[16rem] w-[44rem] rounded-full blur-[120px] opacity-45"
        style={{ background: "radial-gradient(circle, #7A2BE2 0%, rgba(122,43,226,0) 70%)" }}
      />
      {vivid && <>
        <div className="cosmos-nebula" />
        <svg className="cosmos-galaxy absolute inset-0 h-full w-full" viewBox="0 0 1200 900" preserveAspectRatio="none" fill="#D9DDFF">
          {dust.map((star, i) => <circle key={i} cx={star.x} cy={star.y} r={star.r} opacity={star.opacity} />)}
        </svg>
      </>}

      {/* starfield */}
      <div className="cosmos-starfield absolute inset-0">
      {stars.map((s, i) => (
        <span
          key={i}
          className={`cosmos-star absolute rounded-full ${s.bright ? "cosmos-star-bright" : ""}`}
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            height: `${s.size}px`,
            width: `${s.size}px`,
            opacity: s.op,
            backgroundColor: s.color,
            color: s.color,
          }}
        />
      ))}

      {/* larger 4-point sparkles */}
      {sparkles.map((s, i) => (
        <svg
          key={`sp${i}`}
          className="cosmos-sparkle absolute"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
          }}
          viewBox="0 0 24 24"
          fill="white"
        >
          <path d="M12 0 L13.6 10.4 L24 12 L13.6 13.6 L12 24 L10.4 13.6 L0 12 L10.4 10.4 Z" />
        </svg>
      ))}
      </div>
      {vivid && <>
        <span className="cosmos-meteor" />
        <div className="cosmos-horizon" />
      </>}
    </div>
  );
};
